import axios from "axios";
import React, { useState, useEffect } from "react";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { Building2 } from "lucide-react";
import Link from "next/link";
import { ArrowLongLeftIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/navigation";

function Login() {
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  // Company picker state
  const [companies, setCompanies] = useState([]);
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingPassword, setPendingPassword] = useState("");
  const router = useRouter();

  const storeAndRedirect = (data) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("username", data.username);
    localStorage.setItem("access", data.access);
    window.location.href = "/dashboard";
  };

  const handleSubmit = async (e) => {
    setLoading(true);
    e.preventDefault();

    const email = e.target.email.value;
    const password = e.target.password.value;

    const req = await axios.post("https://backend.kobiworks.com/user/login", {
      email,
      password,
    });

    if (req["data"]["message"] === "success") {
      storeAndRedirect(req["data"]);
      return;
    }

    if (req["data"]["message"] === "selectCompany") {
      setPendingEmail(email);
      setPendingPassword(password);
      setCompanies(req["data"]["companies"]);
      setLoading(false);
      return;
    }

    setLoading(false);
    setMessage(req["data"]["message"]);
  };

  const handleSelectCompany = async (companyId) => {
    setLoading(true);
    try {
      const req = await axios.post(
        "https://backend.kobiworks.com/user/select-company",
        { email: pendingEmail, password: pendingPassword, companyId },
      );

      if (req["data"]["message"] === "success") {
        storeAndRedirect(req["data"]);
        return;
      }

      setMessage(req["data"]["message"] || "Something went wrong.");
    } catch (err) {
      setMessage(
        err?.response?.data?.details?.[0]?.message ||
          err?.response?.data?.error ||
          "Unable to sign in. Please go back and try again.",
      );
    } finally {
      setLoading(false);
      setCompanies([]);
    }
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      return (window.location.href = "/dashboard");
    }
  }, []);

  // Company picker screen
  if (companies.length > 0) {
    return (
      <div
        className="w-full h-screen"
        style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      >
        <div className="flex min-h-full flex-1">
          <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
            <div className="mx-auto w-full max-w-sm lg:w-96">
              <div>
                <img
                  alt="Your Company"
                  src="https://i.imgur.com/gidZgkr.jpeg"
                  className="h-8 w-auto rounded"
                />
                <h2
                  className="mt-7 text-[22px] text-[#0f0f14]"
                  style={{ fontWeight: 600, letterSpacing: "-0.3px" }}
                >
                  Select a company
                </h2>
                <p className="mt-1 text-[13px] text-[#6b6b76]">
                  Your account has access to multiple companies.
                </p>
              </div>
              <div className="mt-5 space-y-1">
                {companies.map((company) => (
                  <button
                    key={company.companyId}
                    onClick={() => handleSelectCompany(company.companyId)}
                    disabled={loading}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#e4e4e7] bg-white hover:border-[#d4d4d8] transition-colors text-left group"
                  >
                    {company.imageUrl ? (
                      <img
                        src={company.imageUrl}
                        alt=""
                        className="w-7 h-7 rounded object-cover shrink-0 border border-[#e4e4e7]"
                      />
                    ) : (
                      <span className="w-7 h-7 rounded bg-[#5e6ad2] flex items-center justify-center shrink-0">
                        <Building2 size={13} className="text-white" />
                      </span>
                    )}
                    <span
                      className="text-[13px] text-[#0f0f14]"
                      style={{ fontWeight: 500 }}
                    >
                      {company.title}
                    </span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  setCompanies([]);
                  setMessage("");
                }}
                className="mt-4 text-[12px] text-[#6b6b76] hover:text-[#0f0f14] transition-colors"
              >
                Back to login
              </button>
            </div>
          </div>
          <div className="relative hidden w-0 flex-1 lg:block">
            <img
              alt=""
              src="https://i.imgur.com/y29QIMt.jpeg"
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen">
      <div className="flex min-h-full flex-1">
        <div
          onClick={() => router.back()}
          className="text-xs absolute top-4 left-4 flex justify-center items-center hover:text-blue-400 duration-300 cursor-pointer"
        >
          <ArrowLongLeftIcon className="h-5 w-5 mr-2" /> Go Back
        </div>
        <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <img
                alt="Your Company"
                src="https://i.imgur.com/gidZgkr.jpeg"
                className="h-10 w-auto rounded-full"
              />
              <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                Sign in to your account
              </h2>
            </div>
            {message ? (
              <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <XCircleIcon
                      aria-hidden="true"
                      className="h-5 w-5 text-red-400"
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      {message}
                    </h3>
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
            <div className="mt-4">
              <div>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Email address
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        className="px-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium leading-6 text-gray-900"
                    >
                      Password
                    </label>
                    <div className="mt-2">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        required
                        autoComplete="current-password"
                        className="px-2 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between"></div>

                  {!loading ? (
                    <div>
                      <button
                        type="submit"
                        className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      >
                        Sign in
                      </button>
                    </div>
                  ) : (
                    <div className="animate-pulse">
                      <button className="bg-gray-600 px-3 rounded-md w-full shadow-sm leading-6 py-1.5 text-sm font-semibold text-gray-900">
                        Loading...
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
            <Link href="/signup">
              <div className="w-full mt-4 text-sm cursor-pointer hover:underline duration-300">
                <h4>
                  New here?: <span className="text-blue-500">Sign up</span>
                </h4>
              </div>
            </Link>
            <Link href="/forgotPassword">
              <div className="w-full mt-2 text-sm cursor-pointer hover:underline duration-300">
                <h4>
                  Forgot Password?:{" "}
                  <span className="text-blue-500">Reset Password</span>
                </h4>
              </div>
            </Link>
          </div>
        </div>
        <div className="relative hidden w-0 flex-1 lg:block">
          <img
            alt=""
            src="https://i.imgur.com/y29QIMt.jpeg"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
      </div>
    </div>
  );
}

export default Login;
